import api from '../config/api';

/**
 * Vehicles Service
 * Handles all vehicle-related API calls
 */

/**
 * Get all vehicles with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {string} params.search - Search query
 * @param {string} params.status - Filter by status
 * @param {string} params.agency_id - Filter by agency ID
 * @returns {Promise} API response with vehicles data and pagination
 */
export const getVehicles = async (params = {}) => {
  try {
    const { page = 1, limit = 10, search, status, agency_id } = params;
    
    // Calculate offset for pagination
    const offset = (page - 1) * limit;
    
    // Build query parameters
    const queryParams = {
      limit,
      offset,
    };
    
    if (search) queryParams.search = search;
    if (status) queryParams.status = status;
    if (agency_id) queryParams.agency_id = agency_id;
    
    const response = await api.get('/api/vehicles', { params: queryParams });
    
    // API returns { success, message, data, pagination }
    // response is the axios response object, so we need response.data
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch vehicles. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single vehicle by ID
 * @param {number|string} id - Vehicle ID
 * @returns {Promise} API response with vehicle data
 */
export const getVehicleById = async (id, forceRefresh = false) => {
  try {
    const opts = {};
    if (forceRefresh) {
      // add cache-busting query param
      opts.params = { t: Date.now() };
    }
    const response = await api.get(`/api/vehicles/${id}`, opts);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to fetch vehicle. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new vehicle
 * @param {Object} vehicleData - Vehicle data
 * @param {File|null} vehicleData.photo - Photo file (optional)
 * @returns {Promise} API response with created vehicle data
 */
export const createVehicle = async (vehicleData) => {
  try {
    // Determine if any file is present (photo or any document file)
    const hasAnyFile = Boolean(
      (vehicleData.photo && vehicleData.photo instanceof File) ||
      (vehicleData.documents && Object.values(vehicleData.documents).some(d => d?.file instanceof File))
    );

    let payload;
    let config = {};

    if (hasAnyFile) {
      // Use FormData for file upload
      payload = new FormData();
      payload.append('vehicle_number', vehicleData.vehicle_number || vehicleData.vehicle_number || '');
      payload.append('vehicle_type', vehicleData.vehicle_type || '');
      payload.append('capacity_kg', vehicleData.capacity_kg || 0);
      payload.append('agency_id', vehicleData.agency_id || '');
      if (vehicleData.assigned_driver_id) {
        payload.append('assigned_driver_id', vehicleData.assigned_driver_id);
      }
      payload.append('status', vehicleData.status || 'Active');

      // Append document metadata and files if present
      if (vehicleData.documents && typeof vehicleData.documents === 'object') {
        Object.entries(vehicleData.documents).forEach(([key, doc]) => {
          // metadata keys like documents[rcBook][document_number], documents[rcBook][expiry_date]
          if (doc?.documentNumber) {
            payload.append(`documents[${key}][document_number]`, doc.documentNumber);
          }
          if (doc?.date) {
            payload.append(`documents[${key}][expiry_date]`, doc.date);
          }
          if (doc?.file && doc.file instanceof File) {
            payload.append(`documents[${key}][file]`, doc.file);
          }
        });
      }

      // Append rc/insurance fields for backward compatibility if present
      const docFields = [
        'rc_book_number',
        'insurance_number',
        'pollution_certificate_number',
        'fitness_certificate_number',
        'permit_number',
        'road_tax_number'
      ];
      docFields.forEach((field) => {
        if (vehicleData[field]?.trim()) {
          payload.append(field, vehicleData[field].trim());
        }
      });

      // Append photo if present
      if (vehicleData.photo && vehicleData.photo instanceof File) {
        payload.append('photo', vehicleData.photo);
      }
      // Content-Type will be automatically set by axios interceptor for FormData
    } else {
      // Use JSON for regular data
      payload = { ...vehicleData };
      // Remove photo if it's not a file (e.g., if it's a URL string)
      if (payload.photo && !(payload.photo instanceof File)) {
        delete payload.photo;
      }
      // Remove document file objects if present (we only send metadata in JSON)
      if (payload.documents) {
        Object.keys(payload.documents).forEach(k => {
          if (payload.documents[k]?.file) {
            delete payload.documents[k].file;
          }
        });
      }
    }
    
    const response = await api.post('/api/vehicles', payload, config);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create vehicle. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a vehicle
 * @param {number|string} id - Vehicle ID
 * @param {Object} vehicleData - Updated vehicle data
 * @returns {Promise} API response with updated vehicle data
 */
export const updateVehicle = async (id, vehicleData) => {
  try {
    // If vehicleData contains files, send as FormData
    let config = {};
    if (vehicleData instanceof FormData) {
      const response = await api.put(`/api/vehicles/${id}`, vehicleData, config);
      return response.data || response;
    }

    const hasAnyFile = Boolean(
      (vehicleData.photo && vehicleData.photo instanceof File) ||
      (vehicleData.documents && Object.values(vehicleData.documents).some(d => d?.file instanceof File))
    );

    if (hasAnyFile) {
      const formData = new FormData();
      // append basic fields
      formData.append('vehicle_number', vehicleData.vehicle_number || vehicleData.vehicleNumber || '');
      formData.append('vehicle_type', vehicleData.vehicle_type || vehicleData.vehicleType || '');
      formData.append('capacity_kg', vehicleData.capacity_kg || vehicleData.capacity || 0);
      formData.append('agency_id', vehicleData.agency_id || vehicleData.agencyId || vehicleData.agency || '');
      if (vehicleData.assigned_driver_id || vehicleData.assignedDriverId) {
        formData.append('assigned_driver_id', vehicleData.assigned_driver_id || vehicleData.assignedDriverId);
      }
      formData.append('status', vehicleData.status || 'Active');

      if (vehicleData.photo && vehicleData.photo instanceof File) {
        formData.append('photo', vehicleData.photo);
      }

      if (vehicleData.documents && typeof vehicleData.documents === 'object') {
        Object.entries(vehicleData.documents).forEach(([key, doc]) => {
          if (doc?.documentNumber) {
            formData.append(`documents[${key}][document_number]`, doc.documentNumber);
          }
          if (doc?.date) {
            formData.append(`documents[${key}][expiry_date]`, doc.date);
          }
          if (doc?.file && doc.file instanceof File) {
            formData.append(`documents[${key}][file]`, doc.file);
          }
        });
      }

      const response = await api.put(`/api/vehicles/${id}`, formData, config);
      return response.data || response;
    }

    const response = await api.put(`/api/vehicles/${id}`, vehicleData);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update vehicle. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a vehicle
 * @param {number|string} id - Vehicle ID
 * @returns {Promise} API response
 */
export const deleteVehicle = async (id) => {
  try {
    const response = await api.delete(`/api/vehicles/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete vehicle. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Save vehicle documents (expiry dates, document numbers, file names) in bulk.
 * @param {number|string} vehicleId - Vehicle primary id
 * @param {Array<Object>} documents - Array of documents: { document_type, document_number, expiry_date, file_name }
 * @returns {Promise} API response
 */
export const saveVehicleDocuments = async (vehicleId, documents = []) => {
  try {
    // The backend expects POST /api/vehicles/documents/:vehicleId
    // and the file field name must be 'document' (this.upload.single('document')).
    if (!vehicleId) {
      throw new Error('Missing vehicleId for saving documents.');
    }

    if (!Array.isArray(documents) || documents.length === 0) {
      return [];
    }

    // Build upload promises and execute in parallel
    const uploadPromises = documents.map((doc, idx) => (async () => {
      const formData = new FormData();
      if (doc.document_type) formData.append('document_type', doc.document_type);
      if (doc.document_number) formData.append('document_number', doc.document_number);
      if (doc.expiry_date) {
        // Convert plain YYYY-MM-DD to ISO datetime to match backend expectation
        const dateStr = doc.expiry_date;
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          formData.append('expiry_date', new Date(dateStr).toISOString());
        } else {
          formData.append('expiry_date', dateStr);
        }
      }
      if (doc.file_name) formData.append('file_name', doc.file_name);
      if (doc.file && doc.file instanceof File) {
        formData.append('document', doc.file); // backend expects 'document' field
      }

      console.debug(`saveVehicleDocuments: ${doc.id ? 'PUT' : 'POST'} /api/vehicles/documents/${doc.id || vehicleId} (doc ${idx})`);
      try {
        let response;
        // If doc has an id, update existing document; otherwise create new for the vehicle
        if (doc.id) {
          response = await api.put(`/api/vehicles/documents/${doc.id}`, formData, {
            headers: { Accept: 'application/json' }
          });
        } else {
          response = await api.post(`/api/vehicles/documents/${vehicleId}`, formData, {
            headers: { Accept: 'application/json' }
          });
        }
        
        // Check content-type first - if HTML, the route doesn't exist (routed to frontend)
        const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'] || '';
        if (typeof contentType === 'string' && contentType.includes('text/html')) {
          // HTML response means backend route doesn't exist - this is an error
          return { 
            success: false, 
            error: `Backend route not found: ${doc.id ? 'PUT' : 'POST'} /api/vehicles/documents/${doc.id || vehicleId}. The request was routed to frontend instead of backend API.`, 
            raw: response.data 
          };
        }
        
        // Check HTTP status code
        const status = response.status || (response.response && response.response.status);
        if (status >= 200 && status < 300) {
          // Success status code with JSON response
          const body = response.data || response;
          if (body && typeof body === 'object' && body.success === false) {
            return { success: false, error: body.message || 'Failed to upload document', raw: body };
          }
          return { success: true, data: body };
        }
        
        // Non-success status code
        const body = response.data || response;
        if (body && typeof body === 'object' && body.success === false) {
          return { success: false, error: body.message || 'Failed to upload document', raw: body };
        }
        return { success: false, error: `Request failed with status ${status}`, raw: body };
      } catch (err) {
        // Check if error response is HTML (route doesn't exist)
        const contentType = err.response?.headers?.['content-type'] || err.response?.headers?.['Content-Type'] || '';
        if (typeof contentType === 'string' && contentType.includes('text/html')) {
          return { 
            success: false, 
            error: `Backend route not found: ${doc.id ? 'PUT' : 'POST'} /api/vehicles/documents/${doc.id || vehicleId}. Please check backend route configuration.`, 
            raw: err.response?.data || null 
          };
        }
        
        // Extract detailed error information
        const status = err.response?.status;
        const errorData = err.response?.data || {};
        const errorMessage = errorData.message || errorData.error || err.message || 'Upload failed';
        const errorDetails = errorData.details || errorData.stack || '';
        
        // Log full error for debugging
        console.error(`Document ${idx} upload error:`, {
          status,
          message: errorMessage,
          details: errorDetails,
          fullError: errorData,
          requestUrl: `${doc.id ? 'PUT' : 'POST'} /api/vehicles/documents/${doc.id || vehicleId}`
        });
        
        // Return detailed error message
        let detailedError = errorMessage;
        if (errorDetails) {
          detailedError += ` (Details: ${errorDetails})`;
        }
        if (status) {
          detailedError += ` [Status: ${status}]`;
        }
        
        return { success: false, error: detailedError, raw: errorData };
      }
    })());

    const results = await Promise.all(uploadPromises);

    const failed = results.filter(r => !r.success);
    if (failed.length > 0) {
      const messages = failed.map((f, i) => `doc${i}: ${f.error}`).join('; ');
      throw new Error(`Failed to upload ${failed.length}/${results.length} documents: ${messages}`);
    }

    return results.map(r => r.data);
  } catch (error) {
    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to save vehicle documents. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * List all vehicle documents with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.vehicle_id - Filter by vehicle ID (optional)
 * @param {string} params.document_type - Filter by document type (optional)
 * @param {number} params.limit - Items per page (optional)
 * @param {number} params.offset - Offset for pagination (optional)
 * @returns {Promise} API response with vehicle documents
 */
export const listVehicleDocuments = async (params = {}) => {
  try {
    const response = await api.get('/api/vehicle-documents', { params });
    return response.data || response;
  } catch (error) {
    const errorMessage = error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to list vehicle documents. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Fetch vehicle documents for a vehicle id.
 * Tries GET /api/vehicles/documents/:vehicleId first, falls back to /api/vehicle_document?vehicle_id=<id>
 */
export const getVehicleDocuments = async (vehicleId) => {
  try {
    if (!vehicleId) return [];
    // Preferred route per backend: /api/vehicle-documents?vehicle_id=
    try {
      const res = await api.get('/api/vehicle-documents', { params: { vehicle_id: vehicleId, limit: 20, offset: 0 } });
      if (res.data && (res.data.data || Array.isArray(res.data))) {
        return res.data.data || res.data;
      }
      return res.data || [];
    } catch (errPrimary) {
      console.warn('Primary vehicle-documents endpoint failed, trying legacy endpoints', errPrimary);
      // Try /api/vehicles/documents/:vehicleId
      try {
        const res2 = await api.get(`/api/vehicles/documents/${vehicleId}`);
        if (res2.data && (res2.data.data || Array.isArray(res2.data))) {
          return res2.data.data || res2.data;
        }
        return res2.data || [];
      } catch (err2) {
        console.warn('Secondary vehicles/documents endpoint failed, trying /api/vehicle_document', err2);
        const res3 = await api.get('/api/vehicle_document', { params: { vehicle_id: vehicleId } });
        if (res3.data && (res3.data.data || Array.isArray(res3.data))) {
          return res3.data.data || res3.data;
        }
        return res3.data || [];
      }
    }
  } catch (error) {
    console.error('getVehicleDocuments error', error);
    throw error;
  }
};

